import { useEffect, useState } from "react";
import { Chip, styled, useMediaQuery } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { CustomText } from "../../../styles/CustomText";
import {
  GRAYPALEBACKGROUND1,
  GRAYPALEBORDER,
  GRAYPALECONTENTS,
  GRAYPALEDISABLED,
  GRAYPALETITLE,
  GREENBOX,
  GREENCONTENTS,
  GREENTITLE,
  ORANGECONTENTS,
} from "../../../styles/color";
import { useSasToken } from "../../../hooks/useSasToken";
import {
  VehiclePhotoFields,
  VehiclePhotoInfo,
  VehiclePhotoPhase,
} from "../../../system/types/Vehicle";
import { ANGLE_LABEL, PHOTO_PHASE_LABEL, PHOTO_PHASE_SHORT } from "./vehiclePhoto";
import {
  VIEWER_TABS,
  ViewerTab,
  ViewerTarget,
  etcItems,
  photoAt,
  stepTab,
  tabStatus,
} from "./photoViewerModel";
import { photoTimeLabel } from "../../Common/Photo/photoTime";
import VehiclePhotoZoom, { photoSetsOf } from "./VehiclePhotoZoom";
import ControlHints from "../../Common/Photo/ControlHints";

interface VehiclePhotoViewerProps extends VehiclePhotoFields {
  status: string;
  startMileage?: number | null;
  endMileage?: number | null;
  unitType?: string;
}

const STATUS_COLOR = {
  both: GREENCONTENTS,
  one: ORANGECONTENTS,
  none: GRAYPALEDISABLED,
};

/**
 * 차량 사진 보기. 방향 탭으로 앞/뒤/좌/우/추가를 고르면 출발·도착 사진을 크게 나란히 보여준다.
 * 사진을 누르면 크게 보기(VehiclePhotoZoom)가 열린다.
 * 마이페이지 "사진 보기"와 관리자 운행 상세가 같이 쓴다.
 */
function VehiclePhotoViewer({
  status,
  photoRequired,
  startedByAdmin,
  endedByAdmin,
  startPhotos,
  endPhotos,
  startMileage,
  endMileage,
  unitType,
}: VehiclePhotoViewerProps) {
  const { appendSas } = useSasToken();
  const isMobile = useMediaQuery("(max-width:620px)");
  const sets = photoSetsOf(startPhotos, endPhotos);
  const etc = etcItems(sets);
  const [tab, setTab] = useState<ViewerTab>("FRONT");
  const [zoomTarget, setZoomTarget] = useState<ViewerTarget | null>(null);

  // 비교 화면에서도 ←/→ 로 방향 탭을 넘긴다. 크게 보기가 열려 있으면 그쪽이 처리한다.
  useEffect(() => {
    if (zoomTarget) return;
    const onKey = (event: KeyboardEvent) => {
      const tag = (event.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.key === "ArrowLeft") setTab((current) => stepTab(current, -1));
      if (event.key === "ArrowRight") setTab((current) => stepTab(current, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomTarget]);

  const hasAnyPhoto = startPhotos?.length || endPhotos?.length;
  if (!photoRequired && !hasAnyPhoto) {
    return (
      <Notice>
        <CustomText type="subtitle" bold400 color={GRAYPALECONTENTS}>
          임원용 차량은 사진 대상이 아닙니다.
        </CustomText>
      </Notice>
    );
  }

  const reached = (phase: VehiclePhotoPhase) =>
    phase === "START" ? status === "이용중" || status === "이용완료" : status === "이용완료";
  const byAdmin = (phase: VehiclePhotoPhase) => (phase === "START" ? startedByAdmin : endedByAdmin);
  const mileage = (phase: VehiclePhotoPhase) => (phase === "START" ? startMileage : endMileage);

  const emptyText = (phase: VehiclePhotoPhase) => {
    if (!reached(phase)) return phase === "START" ? "운행 시작 전" : "운행 종료 전";
    if (byAdmin(phase)) return "관리자 처리 · 사진 없음";
    return "사진 없음";
  };

  const phaseNote = (phase: VehiclePhotoPhase) => {
    if (byAdmin(phase)) {
      return <Chip label="관리자 처리" size="small" color="warning" variant="outlined" />;
    }
    const photos = phase === "START" ? sets.START : sets.END;
    if (photoRequired && reached(phase) && photos.length === 0) {
      return <Chip label="사진 없음" size="small" color="error" variant="outlined" />;
    }
    return null;
  };

  const caption = (phase: VehiclePhotoPhase, photo?: VehiclePhotoInfo) => {
    const parts: string[] = [];
    if (photo) parts.push(photoTimeLabel(photo, false));
    const value = mileage(phase);
    if (value !== null && value !== undefined && reached(phase)) {
      parts.push(`${value.toLocaleString()} ${unitType ?? ""}`.trim());
    }
    return parts.join(" · ");
  };

  const panel = (phase: VehiclePhotoPhase) => {
    if (tab === "ETC") return null;
    const photo = photoAt(sets, tab, phase);
    return (
      <Panel key={phase}>
        <PanelHead>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <CustomText type="subtitle" bold500 color={GRAYPALETITLE}>
              {PHOTO_PHASE_LABEL[phase]}
            </CustomText>
            {phaseNote(phase)}
          </span>
          <CustomText type="small" color={GRAYPALECONTENTS}>
            {caption(phase, photo)}
          </CustomText>
        </PanelHead>
        <Frame>
          {photo ? (
            <FrameButton
              type="button"
              onClick={() => setZoomTarget({ kind: "angle", angle: tab, phase })}
              aria-label={`${ANGLE_LABEL[tab]} ${PHOTO_PHASE_LABEL[phase]} 크게 보기`}
            >
              <img src={appendSas(photo.url)} alt="" />
            </FrameButton>
          ) : (
            <FrameEmpty>
              <ImageNotSupportedIcon sx={{ fontSize: 36, color: GRAYPALEDISABLED }} />
              <CustomText type="subtitle" bold500 color={GRAYPALECONTENTS}>
                {emptyText(phase)}
              </CustomText>
            </FrameEmpty>
          )}
        </Frame>
      </Panel>
    );
  };

  const etcSection = (phase: VehiclePhotoPhase) => {
    const items = etc
      .map((item, index) => ({ ...item, index }))
      .filter((item) => item.phase === phase);
    return (
      <Panel key={phase}>
        <PanelHead>
          <CustomText type="subtitle" bold500 color={GRAYPALETITLE}>
            {PHOTO_PHASE_SHORT[phase]} 추가 사진
          </CustomText>
          <CustomText type="small" color={GRAYPALECONTENTS}>
            {items.length}장
          </CustomText>
        </PanelHead>
        <Frame>
          {items.length > 0 ? (
            <EtcGrid>
              {items.map((item) => (
                <EtcThumb
                  key={item.photo.id}
                  type="button"
                  onClick={() => setZoomTarget({ kind: "etc", index: item.index })}
                  aria-label={`${PHOTO_PHASE_SHORT[phase]} 추가 사진 크게 보기`}
                >
                  <img src={appendSas(item.photo.url)} alt="" />
                </EtcThumb>
              ))}
            </EtcGrid>
          ) : (
            <FrameEmpty>
              <PhotoLibraryOutlinedIcon sx={{ fontSize: 36, color: GRAYPALEDISABLED }} />
              <CustomText type="subtitle" bold500 color={GRAYPALECONTENTS}>
                추가 사진이 없습니다
              </CustomText>
              <CustomText type="small" color={GRAYPALECONTENTS}>
                흠집을 가까이 찍은 사진이 있으면 여기에 보입니다
              </CustomText>
            </FrameEmpty>
          )}
        </Frame>
      </Panel>
    );
  };

  const prevTab = stepTab(tab, -1);
  const nextTab = stepTab(tab, 1);

  return (
    <Root>
      <Tabs role="tablist" aria-label="사진 방향">
        {VIEWER_TABS.map((value) => (
          <TabButton
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            selected={tab === value}
            onClick={() => setTab(value)}
          >
            <Dot style={{ backgroundColor: STATUS_COLOR[tabStatus(sets, value)] }} />
            {ANGLE_LABEL[value]}
            {value === "ETC" && etc.length > 0 ? ` ${etc.length}` : ""}
          </TabButton>
        ))}
      </Tabs>

      <Pair mobile={isMobile}>
        {tab === "ETC"
          ? (["START", "END"] as const).map(etcSection)
          : (["START", "END"] as const).map(panel)}
      </Pair>

      <Nav>
        <NavButton type="button" onClick={() => setTab(prevTab)} aria-label={`이전: ${ANGLE_LABEL[prevTab]}`}>
          <ChevronLeftIcon fontSize="small" />
          {ANGLE_LABEL[prevTab]}
        </NavButton>
        <NavButton type="button" onClick={() => setTab(nextTab)} aria-label={`다음: ${ANGLE_LABEL[nextTab]}`}>
          {ANGLE_LABEL[nextTab]}
          <ChevronRightIcon fontSize="small" />
        </NavButton>
      </Nav>
      <ControlHints
        items={
          isMobile
            ? [{ icon: <TouchAppOutlinedIcon />, label: "사진을 누르면 크게 보기" }]
            : [
                { keys: ["←", "→"], label: "방향 이동" },
                { keys: ["클릭"], label: "사진 크게 보기" },
              ]
        }
      />

      {zoomTarget && (
        <VehiclePhotoZoom
          target={zoomTarget}
          sets={sets}
          isMobile={isMobile}
          onChange={(next) => {
            setZoomTarget(next);
            // 크게 보기에서 방향을 옮기면 닫았을 때 비교 화면도 그 방향에 있게 한다
            if (next.kind === "angle") setTab(next.angle);
          }}
          onClose={() => setZoomTarget(null)}
        />
      )}
    </Root>
  );
}

export default VehiclePhotoViewer;

const Root = styled("div")({
  width: "100%",
  marginTop: "10px",
});

const Notice = styled("div")({
  marginTop: "10px",
});

const Tabs = styled("div")({
  display: "flex",
  flexWrap: "wrap",
  gap: "6px",
  marginBottom: "12px",
});

const TabButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ selected }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "6px 14px",
  borderRadius: "8px",
  fontSize: "0.9rem",
  cursor: "pointer",
  border: `1px solid ${selected ? GREENCONTENTS : GRAYPALEBORDER}`,
  backgroundColor: selected ? GREENBOX : "#fff",
  color: selected ? GREENTITLE : GRAYPALETITLE,
  fontWeight: selected ? 600 : 400,
}));

const Dot = styled("span")({
  width: "7px",
  height: "7px",
  borderRadius: "50%",
  display: "inline-block",
});

const Pair = styled("div", {
  shouldForwardProp: (prop) => prop !== "mobile",
})<{ mobile: boolean }>(({ mobile }) => ({
  display: "flex",
  flexDirection: mobile ? "column" : "row",
  gap: "14px",
}));

const Panel = styled("div")({
  flex: 1,
  minWidth: 0,
});

const PanelHead = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "8px",
  minHeight: "28px",
  marginBottom: "6px",
});

// 4:3 칸. aspect-ratio 를 못 쓰는 구형 브라우저를 위해 padding 으로 높이를 잡는다.
const Frame = styled("div")({
  position: "relative",
  width: "100%",
  paddingTop: "75%",
  borderRadius: "8px",
  overflow: "hidden",
  border: `1px solid ${GRAYPALEBORDER}`,
  backgroundColor: GRAYPALEBACKGROUND1,
});

const FrameButton = styled("button")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  padding: 0,
  border: 0,
  background: "transparent",
  cursor: "zoom-in",
  "& img": {
    width: "100%",
    height: "100%",
    // 증빙 사진이라 잘라내지 않고 전체를 보여준다
    objectFit: "contain",
    display: "block",
  },
});

const FrameEmpty = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  padding: "0 12px",
  textAlign: "center",
  boxSizing: "border-box",
});

// 4:3 칸을 꽉 채우는 3열 격자. 단계당 추가 사진은 최대 6장이라 두 줄에 들어간다.
const EtcGrid = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  padding: "8px",
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  alignContent: "start",
  gap: "8px",
  overflowY: "auto",
  boxSizing: "border-box",
});

const EtcThumb = styled("button")({
  position: "relative",
  width: "100%",
  paddingTop: "100%",
  border: `1px solid ${GRAYPALEBORDER}`,
  borderRadius: "8px",
  overflow: "hidden",
  cursor: "zoom-in",
  background: GRAYPALEBACKGROUND1,
  "& img": {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
});

const Nav = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  margin: "12px 0 8px",
});

const NavButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  gap: "2px",
  padding: "6px 12px",
  borderRadius: "8px",
  border: `1px solid ${GRAYPALEBORDER}`,
  backgroundColor: "#fff",
  color: GRAYPALETITLE,
  fontSize: "0.9rem",
  cursor: "pointer",
  "&:hover": { backgroundColor: GRAYPALEBACKGROUND1 },
});
